import { Download } from "@tamagui/lucide-icons";
import { BlurView } from "expo-blur";
import { useMemo } from "react";
import { ScrollView, YStack } from "tamagui";

import {
  CarePlanContentInput,
  CarePlanMedication,
} from "@/api/generated/models";
import {
  Button,
  CloseButton,
  H2,
  Header,
  Heading,
  Section,
  Separator,
  Text,
} from "@/components";
import AutolinkText from "@/components/AutolinkText";
import BulletedList from "@/components/ListBullet";
import { isLocal } from "@/config/env";
import { formatPhoneNumber, formatZipCode } from "@/utils/formatting";

type CarePlanDisplayProps = {
  content: CarePlanContentInput;
  senderFullNameWithTitle?: string | null;
  onClose?: () => void;
};

const renderCarePlanText = (text: string) => (
  <AutolinkText
    text={text}
    dataTrackingId="care-plan-external-link"
    renderText={(text) => text}
  />
);

const CarePlanDisplay = ({
  content,
  onClose,
  senderFullNameWithTitle,
}: CarePlanDisplayProps) => {
  const pharmacyGroups = useMemo(() => {
    const groups = new Map<
      string,
      {
        pharmacy: CarePlanMedication["pharmacy"];
        medications: CarePlanMedication[];
      }
    >();

    (content.medications || []).forEach((med: CarePlanMedication) => {
      const pharmacy = med.pharmacy;

      const key = pharmacy
        ? `${pharmacy.name}_${pharmacy.address}`
        : "no_pharmacy";

      if (!groups.has(key)) {
        groups.set(key, { pharmacy, medications: [] });
      }

      groups.get(key)!.medications.push(med);
    });

    return Array.from(groups.values());
  }, [content.medications]);

  return (
    <YStack
      height="100%"
      borderRadius="$150"
      backgroundColor="$fillModal"
      position="relative"
    >
      <YStack paddingTop="$200" paddingHorizontal="$200" gap="$150">
        {onClose && (
          <CloseButton onClose={onClose} dataTrackingId="care-plan-close" />
        )}
        <Header gap="$150">
          <H2 size="$lg">{content.diagnosis}</H2>
          <Section gap={0}>
            {senderFullNameWithTitle && (
              <Text size="$sm" lineHeight="$md" color="$textSecondary">
                {senderFullNameWithTitle}
              </Text>
            )}
            <Text size="$sm" lineHeight="$md" color="$textSecondary">
              {new Date(content.created).toLocaleDateString()}
            </Text>
          </Section>
          <Separator borderColor="$fillNeutral" />
        </Header>
      </YStack>
      <ScrollView
        flex={1}
        contentContainerStyle={{
          paddingBottom: "$800",
          paddingHorizontal: "$200",
          paddingTop: "$250",
        }}
      >
        <Section gap="$200">
          {pharmacyGroups.length > 0 && (
            <Section gap="$100">
              <Heading>Your prescriptions have been sent</Heading>

              <Section gap="$100">
                {pharmacyGroups.map((group) => {
                  const groupKey =
                    group.pharmacy?.name ||
                    group.pharmacy?.address ||
                    "no_pharmacy";

                  return (
                    <Section key={groupKey} gap="$100">
                      {group.pharmacy && (
                        <Section gap="$100">
                          <Text fontWeight="500">{group.pharmacy.name}</Text>
                          <Text fontSize="$md" color="$textSecondary">
                            {(() => {
                              const address = group.pharmacy.address;
                              // Extract zip code from end of address and format it
                              const parts = address.split(", ");
                              if (parts.length > 0) {
                                const lastPart = parts[parts.length - 1];
                                const zipMatch = lastPart.match(/\d{5,9}$/);
                                if (zipMatch) {
                                  const formattedZip = formatZipCode(
                                    zipMatch[0],
                                  );
                                  return address.replace(
                                    zipMatch[0],
                                    formattedZip,
                                  );
                                }
                              }
                              return address;
                            })()}
                          </Text>
                          <Text fontSize="$md" color="$textSecondary">
                            {formatPhoneNumber(group.pharmacy.phone_number)}
                          </Text>
                        </Section>
                      )}

                      <BulletedList
                        renderText={renderCarePlanText}
                        items={group.medications.map(
                          (med) => `${med.name} — ${med.patient_instructions}`,
                        )}
                        icon={<BulletedList.Bullet />}
                        rootProps={{ gap: "$100" }}
                      />
                    </Section>
                  );
                })}
              </Section>
            </Section>
          )}

          {content.lab_orders && content.lab_orders.length > 0 && (
            <Section gap="$100">
              <Heading>Lab Orders</Heading>
              <BulletedList
                items={content.lab_orders.map((lab) => lab.name)}
                icon={<BulletedList.Bullet />}
                // rootProps={{ gap: "$100" }}
              />
            </Section>
          )}

          {content.recommendations && content.recommendations.length > 0 && (
            <Section gap="$100">
              <Heading>Recommendations</Heading>
              <BulletedList
                items={content.recommendations}
                renderText={renderCarePlanText}
                icon={<BulletedList.Bullet />}
                rootProps={{ gap: "$100" }}
              />
            </Section>
          )}

          {!!content.follow_up?.message && (
            <Section gap="$100">
              <Heading>Follow-up</Heading>
              {renderCarePlanText(content.follow_up.message)}
            </Section>
          )}
        </Section>
      </ScrollView>

      {isLocal && (
        <BlurView
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            borderBottomRightRadius: 12,
            borderBottomLeftRadius: 12,
          }}
        >
          <Button mode="tertiaryWithIcon" icon={Download}>
            Download
          </Button>
        </BlurView>
      )}
    </YStack>
  );
};

export default CarePlanDisplay;
